import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  disabled?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({ content, children, disabled = false }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (!disabled && content.trim() && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      let x = rect.left + rect.width / 2;
      let y = rect.bottom + 8;

      // Keep within viewport
      const tooltipWidth = 280;
      if (x + tooltipWidth / 2 > window.innerWidth) {
        x = window.innerWidth - tooltipWidth / 2 - 8;
      }
      if (x - tooltipWidth / 2 < 0) {
        x = tooltipWidth / 2 + 8;
      }

      // If too close to bottom, show above
      if (y + 80 > window.innerHeight) {
        y = rect.top - 8;
      }

      setPosition({ x, y });
      setVisible(true);
    }
  };

  const handleMouseLeave = () => {
    setVisible(false);
  };

  const tooltipElement = visible && content.trim() ? (
    <div
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        transform: position.y < (triggerRef.current?.getBoundingClientRect().top ?? 0)
          ? 'translate(-50%, -100%)'
          : 'translate(-50%, 0)',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        padding: '6px 10px',
        borderRadius: '6px',
        fontSize: '12px',
        maxWidth: '280px',
        whiteSpace: 'pre-line',
        lineHeight: '1.4',
        zIndex: 10000,
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
      }}
    >
      {content}
    </div>
  ) : null;

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'inline-block' }}
      >
        {children}
      </div>
      {tooltipElement && createPortal(tooltipElement, document.body)}
    </>
  );
};

export default Tooltip;
